import { ActionType, WorkflowId } from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import {
    activeLinkStyle,
    linkStyle,
} from "components/reports/show/custom-reports/custom-reports-sidebar";
import { InlineBanner } from "components/ui/inline-banner";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { Pill } from "components/ui/pill";
import { PrimaryButton } from "components/ui/primary-button";
import { SearchInput } from "components/ui/search-input";
import { SecondaryButton } from "components/ui/secondary-button";
import { WorkflowExecutionList } from "components/workflow/workflow-execution-list";
import { useGetWorkflowsQuery, WorkflowStatus } from "generated/graphql";
import React from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { navigate } from "store/actions";
import { useProductName } from "store/selectors/branding";
import { useFeature, useIsAllowed } from "store/selectors/hooks";
import { CreateWorkflowPanel } from "./create-workflow-panel";
import { TenantWorkflowSettings } from "./tenant-workflow-settings";
import { UserWorkflowSettings } from "./user-workflow-settings";

interface Props {
    scope: "user" | "tenant";
}
export const WorkflowSettings: React.FC<Props> = props => {
    const { scope } = props;
    const [creatingWorkflow, setCreatingWorkflow] = React.useState(false);
    const { params } = useRouteMatch<{ workflowId?: WorkflowId }>();
    const workflows = useGetWorkflowsQuery();
    const workflowsEnabled = useFeature("workflows");
    const productName = useProductName();
    const [searchText, setSearchText] = React.useState("");

    const handleCreateWorkflow = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setCreatingWorkflow(true);
        },
        [setCreatingWorkflow],
    );

    const handleRefetch = React.useCallback(() => {
        workflows.refetch();
    }, [workflows]);

    const handleSearchChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchText(e.target.value);
        },
        [setSearchText],
    );

    const selectedWorkflowId = params?.workflowId || null;
    const canManageWorkflows = useIsAllowed(ActionType.CreateWorkflow);
    const canReadWorkflows = useIsAllowed(ActionType.ReadWorkflow);

    if (scope === "tenant" && !canManageWorkflows) {
        return null;
    }
    if (scope === "user" && !canReadWorkflows) {
        return null;
    }

    if (!workflowsEnabled) {
        return (
            <ContentFull>
                <ContentHeader>Workflows</ContentHeader>
                <div className="space-y-4">
                    <p>
                        Automate important processes with Workflows. Automatically send
                        follow-up emails, create follow-up tasks, and more.
                    </p>
                    <InlineBanner type="info">
                        Workflows are available to {productName} Premium customers.
                        Contact <SupportEmailLink /> to discuss an upgrade to Premium.
                    </InlineBanner>
                </div>
            </ContentFull>
        );
    }

    return (
        <div className="space-y-4 lg:space-y-8">
            {scope === "tenant" ? (
                <ContentFull>
                    <ContentHeader>Workflows</ContentHeader>
                    <div className="space-y-4">
                        <p>
                            Automate important processes with Workflows. Automatically
                            send follow-up emails, create follow-up tasks, and more.
                        </p>
                        <PrimaryButton onClick={handleCreateWorkflow}>
                            Create workflow
                        </PrimaryButton>
                    </div>
                </ContentFull>
            ) : null}
            <QueryRenderer name="WorkflowSettings" query={workflows}>
                {data => {
                    const scopedWorkflows =
                        scope === "user"
                            ? data.getWorkflows.filter(
                                  w => w.status === WorkflowStatus.Published,
                              )
                            : data.getWorkflows;

                    const selectedWorkflow =
                        scopedWorkflows.find(w => w.id === selectedWorkflowId) ||
                        scopedWorkflows[0] ||
                        null;

                    const filteredWorkflows = scopedWorkflows.filter(workflow =>
                        workflow.name.toLowerCase().includes(searchText.toLowerCase()),
                    );

                    if (!selectedWorkflow) {
                        return (
                            <div className="space-y-4 lg:space-y-8">
                                {scope === "user" ? (
                                    <ContentFull>
                                        <ContentHeader>
                                            Pending workflows for my clients
                                        </ContentHeader>
                                        <WorkflowExecutionList />
                                    </ContentFull>
                                ) : null}
                                <ContentFull>
                                    <ContentHeader>
                                        {scope === "user"
                                            ? "Active Workflows"
                                            : "Agency Workflows"}
                                    </ContentHeader>
                                    <div className="space-y-4">
                                        <InlineBanner type="info">
                                            {scope === "user"
                                                ? canManageWorkflows
                                                    ? "No workflows have been published yet. Use the Manage agency workflows button to create and publish workflows."
                                                    : "No workflows have been published yet. Contact your agency administrator to set up workflows."
                                                : 'No workflows have been created yet. Use the "Create workflow" button above to get started.'}
                                        </InlineBanner>
                                        {scope === "user" && canManageWorkflows ? (
                                            <Link to="/settings/workflow">
                                                <PrimaryButton>
                                                    Manage agency workflows
                                                </PrimaryButton>
                                            </Link>
                                        ) : null}
                                    </div>
                                </ContentFull>
                            </div>
                        );
                    }

                    return (
                        <div className="space-y-4 lg:space-y-8">
                            {scope === "user" ? (
                                <ContentFull>
                                    <ContentHeader>
                                        Pending workflows for my clients
                                    </ContentHeader>
                                    <WorkflowExecutionList />
                                </ContentFull>
                            ) : null}
                            <ContentFull>
                                <div className="flex justify-between items-center">
                                    <ContentHeader>
                                        {scope === "user"
                                            ? "Active Workflows"
                                            : "Agency Workflows"}
                                    </ContentHeader>
                                    {scope === "user" && canManageWorkflows ? (
                                        <Link to="/settings/workflow">
                                            <SecondaryButton>
                                                Manage agency workflows
                                            </SecondaryButton>
                                        </Link>
                                    ) : null}
                                </div>
                                <div className="lg:grid lg:grid-cols-3 lg:gap-x-4">
                                    <div className="lg:col-span-1 space-y-2">
                                        <div className="max-w-sm">
                                            <SearchInput
                                                value={searchText}
                                                onChange={handleSearchChange}
                                            />
                                        </div>
                                        <ul className="space-y-2">
                                            {filteredWorkflows.map(workflow => {
                                                return (
                                                    <li
                                                        key={workflow.id}
                                                        className="pr-4">
                                                        <Link
                                                            className={
                                                                workflow.id ===
                                                                selectedWorkflow.id
                                                                    ? activeLinkStyle
                                                                    : linkStyle
                                                            }
                                                            to={`/settings/${
                                                                scope === "tenant"
                                                                    ? ""
                                                                    : "account/"
                                                            }workflow/${workflow.id}`}>
                                                            <p className="space-x-2">
                                                                <span>
                                                                    {workflow.name}
                                                                </span>
                                                                {workflow.status ===
                                                                WorkflowStatus.Draft ? (
                                                                    <Pill>Draft</Pill>
                                                                ) : null}
                                                            </p>
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                    <div className="lg:col-span-2">
                                        {scope === "tenant" ? (
                                            <TenantWorkflowSettings
                                                workflow={selectedWorkflow}
                                                refetch={handleRefetch}
                                                workflowSettings={
                                                    data.getWorkflowSettings
                                                }
                                                allWorkflows={data.getWorkflows}
                                            />
                                        ) : (
                                            <UserWorkflowSettings
                                                workflow={selectedWorkflow}
                                                refetch={handleRefetch}
                                                workflowSettings={
                                                    data.getWorkflowSettings
                                                }
                                                canEditWorkflow={canManageWorkflows}
                                            />
                                        )}
                                    </div>
                                </div>
                            </ContentFull>
                        </div>
                    );
                }}
            </QueryRenderer>
            <Panel
                type={PanelType.extraLarge}
                headerText="Create workflow"
                isOpen={creatingWorkflow}
                onDismiss={() => setCreatingWorkflow(false)}>
                <CreateWorkflowPanel
                    onSaved={newWorkflow => {
                        setCreatingWorkflow(false);
                        workflows.refetch();
                        navigate(`/settings/workflow/${newWorkflow.id}`);
                    }}
                    onCancel={() => setCreatingWorkflow(false)}
                />
            </Panel>
        </div>
    );
};
